/**
 * iPhone, iPad, and the Android app cannot save a blob through a hidden
 * <a download> click, and they ignore print() inside an off-screen iframe.
 * The installed Android and iOS apps use the native save and print screens.
 * Desktop keeps its own path. These helpers run only on handheld devices.
 */
import { Capacitor, registerPlugin } from '@capacitor/core'

const NativeDocuments = registerPlugin('NativeDocuments')

const SHEET_ID = 'handheld-file-sheet'
const PRINT_ROOT_ID = 'handheld-print-root'

export function isIOSDevice() {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent || ''
  const touchMac = navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1
  return touchMac || /iPhone|iPad|iPod/i.test(ua)
}

export function isHandheldDevice() {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent || ''
  if (isIOSDevice() || /Android/i.test(ua)) return true
  try {
    if (window.Capacitor?.isNativePlatform?.()) return true
  } catch {
    /* ignore */
  }
  return false
}

function safeFilename(name) {
  const cleaned = String(name || 'download')
    .replace(/[\\/:*?"<>|]/g, '_')
    .replace(/\s+/g, ' ')
    .trim()
  return (cleaned || 'download').slice(0, 120)
}

export function asDownloadBlob(blobData, mimeType) {
  if (blobData instanceof Blob) {
    if (mimeType && (!blobData.type || blobData.type === 'application/octet-stream')) {
      return new Blob([blobData], { type: mimeType })
    }
    return blobData
  }
  return new Blob([blobData], { type: mimeType || 'application/octet-stream' })
}

function toFile(blob, filename) {
  const name = safeFilename(filename)
  const type = blob.type || 'application/octet-stream'
  return new File([blob], name, { type })
}

function canShareFile(file) {
  if (!file || !navigator.share || !navigator.canShare) return false
  try {
    return navigator.canShare({ files: [file] })
  } catch {
    return false
  }
}

async function shareFile(file) {
  if (!canShareFile(file)) return false
  try {
    await navigator.share({ files: [file], title: file.name })
    return true
  } catch (error) {
    if (error?.name === 'AbortError') return 'cancelled'
    return false
  }
}

function triggerAnchorDownload(blob, filename) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', safeFilename(filename))
  link.target = '_blank'
  link.rel = 'noopener'
  document.body.appendChild(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

function removeSheet() {
  document.getElementById(SHEET_ID)?.remove()
}

function buttonStyle(primary) {
  return [
    'flex:1',
    'min-height:44px',
    'border:0',
    'border-radius:10px',
    'font:600 15px/1.2 inherit',
    'cursor:pointer',
    primary ? 'background:#1d4ed8;color:#fff' : 'background:#e5e7eb;color:#111827',
  ].join(';')
}

function openActionSheet({ title, message, actions }) {
  removeSheet()
  const overlay = document.createElement('div')
  overlay.id = SHEET_ID
  overlay.style.cssText = [
    'position:fixed',
    'inset:0',
    'z-index:2147483000',
    'background:rgba(15,23,42,.55)',
    'display:flex',
    'align-items:flex-end',
    'justify-content:center',
    'padding:16px',
  ].join(';')

  const card = document.createElement('div')
  card.style.cssText = [
    'width:min(440px,100%)',
    'background:#fff',
    'color:#111827',
    'border-radius:16px',
    'padding:18px',
    'box-shadow:0 16px 40px rgba(0,0,0,.25)',
    'font-family:inherit',
  ].join(';')

  const heading = document.createElement('p')
  heading.textContent = title
  heading.style.cssText = 'margin:0 0 6px;font-size:16px;font-weight:700'

  const copy = document.createElement('p')
  copy.textContent = message
  copy.style.cssText = 'margin:0 0 14px;font-size:13px;line-height:1.45;color:#4b5563'

  const row = document.createElement('div')
  row.style.cssText = 'display:flex;gap:8px;flex-wrap:wrap'

  actions.forEach((action) => {
    if (action.href) {
      const link = document.createElement('a')
      link.href = action.href
      link.setAttribute('download', action.download || 'download')
      link.textContent = action.label
      link.style.cssText = `${buttonStyle(action.primary)};display:flex;align-items:center;justify-content:center;text-decoration:none;text-align:center`
      link.addEventListener('click', () => {
        setTimeout(() => action.onClick?.(), 300)
      })
      row.appendChild(link)
      return
    }
    const btn = document.createElement('button')
    btn.type = 'button'
    btn.textContent = action.label
    btn.style.cssText = buttonStyle(action.primary)
    btn.addEventListener('click', () => {
      action.onClick(btn)
    })
    row.appendChild(btn)
  })

  card.appendChild(heading)
  card.appendChild(copy)
  card.appendChild(row)
  overlay.appendChild(card)
  overlay.addEventListener('click', (event) => {
    if (event.target === overlay) {
      const cancel = actions.find((action) => action.cancel)
      cancel?.onClick()
    }
  })
  document.body.appendChild(overlay)
  return overlay
}

function isNativeApp() {
  try {
    return Capacitor.isNativePlatform()
  } catch {
    return false
  }
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const text = String(reader.result || '')
      const comma = text.indexOf(',')
      resolve(comma >= 0 ? text.slice(comma + 1) : text)
    }
    reader.onerror = () => reject(reader.error || new Error('Could not read the file'))
    reader.readAsDataURL(blob)
  })
}

function isCancelledError(error) {
  return String(error?.message || error || '').toLowerCase().includes('cancel')
}

function documentBridge() {
  const bridge = typeof window !== 'undefined' ? window.DigiQinDocuments : null
  if (bridge && typeof bridge.saveFile === 'function') return bridge
  return null
}

function readNativeResult(result, fallback) {
  if (typeof result === 'string') {
    const text = result.trim()
    if (!text || text.startsWith('error:')) {
      throw new Error(text.replace(/^error:/, '') || 'Could not complete the file action')
    }
    if (text.toLowerCase().includes('cancel')) {
      throw new Error('cancelled')
    }
    return text
  }
  return result?.location || fallback
}

async function nativeSaveFile(blob, filename, mimeType) {
  const data = await blobToBase64(blob)
  const mime = mimeType || blob.type || 'application/octet-stream'
  const bridge = documentBridge()
  if (bridge) {
    return readNativeResult(bridge.saveFile(data, filename, mime), 'Downloads')
  }
  const result = await NativeDocuments.saveFile({ data, filename, mimeType: mime })
  return readNativeResult(result, 'Downloads')
}

async function nativePrintPdf(blob, filename) {
  const data = await blobToBase64(blob)
  const bridge = documentBridge()
  if (bridge && typeof bridge.printPdf === 'function') {
    readNativeResult(bridge.printPdf(data, filename), 'ok')
    return
  }
  await NativeDocuments.printPdf({ data, filename })
}

async function nativePrintHtml(html, title) {
  const bridge = documentBridge()
  if (bridge && typeof bridge.printHtml === 'function') {
    readNativeResult(bridge.printHtml(html, title || 'Document'), 'ok')
    return
  }
  await NativeDocuments.printHtml({ html, title: title || 'Document' })
}

function openPdfAndPrint(blob) {
  if (isNativeApp()) return false
  const url = URL.createObjectURL(blob)
  let popup = null
  try {
    popup = window.open(url, '_blank')
  } catch {
    popup = null
  }
  if (!popup) {
    setTimeout(() => URL.revokeObjectURL(url), 60_000)
    return false
  }
  const printPopup = () => {
    try {
      popup.focus()
      popup.print()
    } catch {
      /* The PDF viewer still lets the user print from the share menu. */
    }
  }
  popup.addEventListener('load', () => setTimeout(printPopup, 400))
  setTimeout(printPopup, 900)
  setTimeout(() => URL.revokeObjectURL(url), 120_000)
  return true
}

export function saveBlobOnHandheld(blobData, filename, mimeType) {
  const blob = asDownloadBlob(blobData, mimeType)
  const file = toFile(blob, filename)
  if (isNativeApp() || documentBridge()) {
    return nativeSaveFile(blob, file.name, blob.type)
      .then((location) => ({ method: 'download', location }))
      .catch((error) => {
        if (isCancelledError(error)) return { method: 'cancelled' }
        return Promise.reject(error)
      })
  }
  return openSaveSheet(blob, file)
}

function openSaveSheet(blob, file) {
  const shareReady = canShareFile(file)
  const fileUrl = shareReady ? '' : URL.createObjectURL(blob)
  return new Promise((resolve) => {
    let settled = false
    const finish = (method) => {
      if (settled) return
      settled = true
      removeSheet()
      if (fileUrl) setTimeout(() => URL.revokeObjectURL(fileUrl), 60_000)
      resolve({ method })
    }
    const save = async (btn) => {
      if (btn) btn.disabled = true
      const shared = await shareFile(file)
      if (shared === true) {
        finish('share')
        return
      }
      if (shared === 'cancelled') {
        if (btn) btn.disabled = false
        return
      }
      triggerAnchorDownload(blob, file.name)
      finish('download')
    }
    const saveAction = shareReady
      ? { label: 'Save', primary: true, onClick: save }
      : {
          label: 'Save',
          primary: true,
          href: fileUrl,
          download: file.name,
          onClick: () => finish('download'),
        }
    openActionSheet({
      title: file.name,
      message: 'Tap Save to store this file on your device. On iPhone and iPad, choose Save to Files.',
      actions: [
        saveAction,
        { label: 'Cancel', cancel: true, onClick: () => finish('cancelled') },
      ],
    })
  })
}

export function printPdfOnHandheld(blobData, filename) {
  const blob = asDownloadBlob(blobData, 'application/pdf')
  const file = toFile(blob, filename)
  if (isNativeApp() || documentBridge()) {
    return nativePrintPdf(blob, file.name)
      .then(() => ({ method: 'print' }))
      .catch((error) => {
        if (isCancelledError(error)) return { method: 'cancelled' }
        return Promise.reject(error)
      })
  }
  return openPrintSheet(blob, file)
}

function openPrintSheet(blob, file) {
  return new Promise((resolve) => {
    let settled = false
    const finish = (method) => {
      if (settled) return
      settled = true
      removeSheet()
      resolve({ method })
    }
    const print = async (btn) => {
      if (btn) btn.disabled = true
      if (openPdfAndPrint(blob)) {
        finish('print')
        return
      }
      const shared = await shareFile(file)
      if (shared === true) {
        finish('print')
        return
      }
      if (shared === 'cancelled') {
        if (btn) btn.disabled = false
        return
      }
      triggerAnchorDownload(blob, file.name)
      finish('download')
    }
    const save = async (btn) => {
      if (btn) btn.disabled = true
      const shared = await shareFile(file)
      if (shared === true) {
        finish('share')
        return
      }
      if (shared === 'cancelled') {
        if (btn) btn.disabled = false
        return
      }
      triggerAnchorDownload(blob, file.name)
      finish('download')
    }
    openActionSheet({
      title: file.name,
      message: 'Tap Print to open the print menu. If that menu is not available, use Save and choose Print or Save to Files.',
      actions: [
        { label: 'Print', primary: true, onClick: print },
        { label: 'Save', onClick: save },
        { label: 'Cancel', cancel: true, onClick: () => finish('cancelled') },
      ],
    })
  })
}

function ensurePrintRoot() {
  let root = document.getElementById(PRINT_ROOT_ID)
  if (!root) {
    root = document.createElement('div')
    root.id = PRINT_ROOT_ID
    root.setAttribute('aria-hidden', 'true')
    document.body.appendChild(root)
  }
  return root
}

function clearPrintRoot() {
  const root = document.getElementById(PRINT_ROOT_ID)
  if (root) root.replaceChildren()
  document.body.classList.remove('handheld-print-mode')
}

function waitForPrintImages(root) {
  const images = Array.from(root.querySelectorAll('img'))
  return Promise.all(images.map((img) => new Promise((resolve) => {
    const finish = () => {
      if (typeof img.decode === 'function' && img.naturalWidth > 0) {
        img.decode().then(resolve).catch(resolve)
        return
      }
      resolve()
    }
    if (img.complete && img.naturalWidth > 0) {
      finish()
      return
    }
    const timer = window.setTimeout(finish, 2000)
    const done = () => {
      window.clearTimeout(timer)
      finish()
    }
    img.addEventListener('load', done, { once: true })
    img.addEventListener('error', done, { once: true })
  })))
}

function mountHtmlForPrint(html) {
  const root = ensurePrintRoot()
  root.replaceChildren()
  const parsed = new DOMParser().parseFromString(html, 'text/html')
  parsed.querySelectorAll('style').forEach((node) => {
    const copy = node.cloneNode(true)
    copy.setAttribute('data-handheld-print-style', 'true')
    root.appendChild(copy)
  })
  const content = document.createElement('div')
  content.innerHTML = parsed.body ? parsed.body.innerHTML : html
  root.appendChild(content)
  // Last, so it wins over the label's own page size. A zero margin stops
  // Chrome from printing the date, document title, and page address.
  if (html.includes('class="sheet') || html.includes('class="card"')) {
    const single = html.includes('class="sheet single"')
    const cardOnly = html.includes('class="card"') && !html.includes('class="sheet"')
    const fit = document.createElement('style')
    fit.setAttribute('data-qr-print-page', 'true')
    fit.textContent = single
      ? '@page{margin:0;size:148.5mm 104.5mm;}@media print{html,body{margin:0!important;padding:0!important;}.sheet.single{width:148.5mm!important;height:104.5mm!important;overflow:hidden!important;}.sheet.single .slot{width:148.5mm!important;height:104.5mm!important;}}'
      : cardOnly
        ? '@page{margin:0;}@media print{html,body{margin:0!important;padding:0!important;}}'
        : '@page{margin:0;size:297mm 210mm;}@media print{html,body{margin:0!important;padding:0!important;}.sheet{width:297mm!important;height:209mm!important;}}'
    root.appendChild(fit)
  }
  document.body.classList.add('handheld-print-mode')
  return root
}

/** Print on this page so Chrome does not add the date, title, or site address. */
export function printHtmlInDocument(html) {
  const root = mountHtmlForPrint(html)
  return waitForPrintImages(root).then(() => new Promise((resolve) => {
    let opened = false
    const cleanup = () => {
      window.removeEventListener('afterprint', onAfter)
      clearPrintRoot()
    }
    const onAfter = () => cleanup()
    window.addEventListener('afterprint', onAfter, { once: true })
    const timer = window.setTimeout(() => {
      if (!opened) {
        cleanup()
        resolve(false)
      }
    }, 1500)
    window.addEventListener('beforeprint', () => {
      opened = true
      window.clearTimeout(timer)
      resolve(true)
    }, { once: true })
    try {
      window.focus()
      window.print()
    } catch {
      cleanup()
      resolve(false)
    }
  }))
}

function printMountedHtml() {
  return new Promise((resolve) => {
    let sawPrint = false
    let settled = false
    let failTimer = 0
    const mq = window.matchMedia('print')
    const finish = (opened) => {
      if (settled) return
      settled = true
      window.clearTimeout(failTimer)
      window.removeEventListener('beforeprint', onBeforePrint)
      if (mq.removeEventListener) mq.removeEventListener('change', onChange)
      else if (mq.removeListener) mq.removeListener(onChange)
      clearPrintRoot()
      resolve(opened)
    }
    const markOpened = () => {
      sawPrint = true
      window.clearTimeout(failTimer)
    }
    const onChange = (event) => {
      if (event.matches) markOpened()
    }
    const onBeforePrint = () => markOpened()
    failTimer = window.setTimeout(() => {
      if (!sawPrint) finish(false)
    }, 1200)
    window.addEventListener('beforeprint', onBeforePrint, { once: true })
    window.addEventListener('afterprint', () => finish(true), { once: true })
    if (mq.addEventListener) mq.addEventListener('change', onChange)
    else if (mq.addListener) mq.addListener(onChange)
    try {
      window.focus()
      window.print()
    } catch {
      finish(false)
    }
  })
}

export function printHtmlOnHandheld(html, title) {
  if (isNativeApp() || documentBridge()) {
    return nativePrintHtml(html, title)
      .then(() => true)
      .catch((error) => {
        if (isCancelledError(error)) return 'cancelled'
        return Promise.reject(error)
      })
  }
  return openHtmlPrintPreview(html, title)
}

function openHtmlPrintPreview(html, title) {
  return new Promise((resolve) => {
    removeSheet()
    const overlay = document.createElement('div')
    overlay.id = SHEET_ID
    overlay.style.cssText = [
      'position:fixed',
      'inset:0',
      'z-index:2147483000',
      'background:#fff',
      'display:flex',
      'flex-direction:column',
    ].join(';')

    const bar = document.createElement('div')
    bar.setAttribute('data-print-chrome', 'true')
    bar.style.cssText = [
      'display:flex',
      'gap:8px',
      'padding:10px',
      'background:#0f172a',
      'flex-shrink:0',
    ].join(';')

    const frame = document.createElement('iframe')
    frame.setAttribute('title', title || 'Print')
    frame.style.cssText = 'flex:1;width:100%;border:0;background:#fff'
    frame.srcdoc = html

    const hint = document.createElement('p')
    hint.style.cssText = 'display:none;margin:0;padding:8px 12px;background:#fef3c7;color:#78350f;font-size:13px'
    hint.textContent = 'The print menu did not open. Tap Save, then choose Print or Save to Files.'

    let settled = false
    const finish = (value) => {
      if (settled) return
      settled = true
      clearPrintRoot()
      overlay.remove()
      resolve(value)
    }

    const printBtn = document.createElement('button')
    printBtn.type = 'button'
    printBtn.textContent = 'Print'
    printBtn.style.cssText = buttonStyle(true)

    const saveBtn = document.createElement('button')
    saveBtn.type = 'button'
    saveBtn.textContent = 'Save'
    saveBtn.style.cssText = buttonStyle(false)

    const closeBtn = document.createElement('button')
    closeBtn.type = 'button'
    closeBtn.textContent = 'Close'
    closeBtn.style.cssText = buttonStyle(false)

    printBtn.addEventListener('click', () => {
      printBtn.disabled = true
      hint.style.display = 'none'
      mountHtmlForPrint(html)
      printMountedHtml().then((opened) => {
        printBtn.disabled = false
        if (opened) finish(true)
        else hint.style.display = 'block'
      })
    })

    saveBtn.addEventListener('click', async () => {
      saveBtn.disabled = true
      const file = new File([html], safeFilename(`${title || 'document'}.html`), { type: 'text/html' })
      const shared = await shareFile(file)
      saveBtn.disabled = false
      if (shared === true) finish(true)
      else if (shared !== 'cancelled') hint.style.display = 'block'
    })

    closeBtn.addEventListener('click', () => finish('cancelled'))

    bar.appendChild(printBtn)
    bar.appendChild(saveBtn)
    bar.appendChild(closeBtn)
    overlay.appendChild(bar)
    overlay.appendChild(hint)
    overlay.appendChild(frame)
    document.body.appendChild(overlay)
  })
}
